import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMfgApi, errMsg } from './mfgApi';
import { useToast } from '../../hooks/useToast';
import { Plus, Loader2, X, Eye, ChevronDown } from 'lucide-react';
import Drawer from '../../components/Drawer';
import { useMfgRole, MfgReadOnlyBadge } from './useMfgRole';

const STATUS_BADGE = {
  pending: 'bg-gray-100 text-gray-600', in_production: 'bg-amber-50 text-amber-700',
  partially_dispatched: 'bg-blue-50 text-blue-700', completed: 'bg-emerald-50 text-emerald-700',
};

export default function MfgOrders() {
  const mfg = useMfgApi();
  const toast = useToast();
  const [sp] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(sp.get('new') === '1');
  const [viewOrder, setViewOrder] = useState(null);
  const [skus, setSkus] = useState([]);
  const { isOwner } = useMfgRole();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [orders, skuList] = await Promise.all([mfg.get('/client-orders'), mfg.get('/skus')]);
      setRows(orders.items || orders || []);
      setSkus((skuList.items || skuList || []).filter(s => s.is_active !== false));
    } catch { toast.show('Failed to load orders', 'error'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="mfg-orders-page">
      <div className="flex items-center justify-between mb-5">
        <h1 className="text-xl font-extrabold text-gray-900 flex items-center gap-2">Client Orders<MfgReadOnlyBadge /></h1>
        {isOwner && <button data-testid="mfg-new-order-btn" onClick={() => setShowNew(true)} className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-indigo-500 text-white text-sm font-bold hover:bg-indigo-600 transition-colors"><Plus size={14} /> New Order</button>}
      </div>

      {loading ? <div className="flex justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-indigo-500" /></div> : (
        <div className="glass-card overflow-x-auto">
          <table className="w-full text-sm" data-testid="orders-table">
            <thead><tr className="text-[10px] uppercase tracking-wider text-gray-500 border-b border-gray-100">
              <th className="text-left py-2.5 px-3">Order No</th><th className="text-left py-2.5 px-3">Client</th>
              <th className="text-left py-2.5 px-3">SKU</th><th className="text-left py-2.5 px-3">Due Date</th>
              <th className="text-right py-2.5 px-3">Total Ctns</th><th className="text-right py-2.5 px-3">Produced</th>
              <th className="text-right py-2.5 px-3">Dispatched</th><th className="text-right py-2.5 px-3">Balance</th>
              <th className="text-center py-2.5 px-3">Status</th><th className="py-2.5 px-3 w-10"></th>
            </tr></thead>
            <tbody>
              {rows.length === 0 ? <tr><td colSpan={10} className="text-center py-12 text-gray-400">No orders yet.</td></tr> : rows.map(r => {
                const p = r.progress || {};
                return (
                  <tr key={r.id} className="border-b border-gray-50 hover:bg-gray-50/50 cursor-pointer" data-testid={`order-row-${r.id}`} onClick={() => setViewOrder(r)}>
                    <td className="py-2.5 px-3 font-bold text-gray-900">{r.order_no}</td>
                    <td className="py-2.5 px-3 text-gray-700">{r.client_name}</td>
                    <td className="py-2.5 px-3 text-gray-600">{r.sku_name || r.sku_code || '—'}</td>
                    <td className="py-2.5 px-3 text-gray-600">{r.delivery_due_date || '—'}</td>
                    <td className="py-2.5 px-3 text-right font-semibold">{r.total_cartons}</td>
                    <td className="py-2.5 px-3 text-right">{p.produced_cartons ?? '—'}</td>
                    <td className="py-2.5 px-3 text-right">{p.dispatched_cartons ?? '—'}</td>
                    <td className="py-2.5 px-3 text-right font-semibold">{p.balance_cartons ?? '—'}</td>
                    <td className="py-2.5 px-3 text-center"><span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${STATUS_BADGE[p.status] || 'bg-gray-100 text-gray-500'}`}>{(p.status || 'pending').replace(/_/g, ' ')}</span></td>
                    <td className="py-2.5 px-3"><Eye size={14} className="text-gray-400" /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showNew && isOwner && <NewOrderModal skus={skus} mfg={mfg} toast={toast} onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />}
      {viewOrder && <OrderDetailDrawer order={viewOrder} mfg={mfg} onClose={() => setViewOrder(null)} />}
    </div>
  );
}

function NewOrderModal({ skus, mfg, toast, onClose, onCreated }) {
  const [form, setForm] = useState({ client_name: '', sku_id: '', total_cartons: '', carton_pack_size: '', delivery_due_date: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  useEffect(() => {
    if (form.sku_id) {
      const sku = skus.find(s => s.id === form.sku_id);
      if (sku?.carton_pack_size_default && !form.carton_pack_size) set('carton_pack_size', String(sku.carton_pack_size_default));
    }
  }, [form.sku_id, skus]);

  const submit = async () => {
    setSaving(true); setError('');
    try {
      await mfg.post('/client-orders', {
        client_name: form.client_name, sku_id: form.sku_id,
        total_cartons: Number(form.total_cartons), carton_pack_size: Number(form.carton_pack_size),
        delivery_due_date: form.delivery_due_date || undefined, notes: form.notes || undefined,
      });
      toast.show('Order created', 'success'); onCreated();
    } catch (e) { setError(errMsg(e)); } finally { setSaving(false); }
  };

  const valid = form.client_name.trim() && form.sku_id && Number(form.total_cartons) > 0 && Number(form.carton_pack_size) > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="new-order-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-extrabold text-gray-900">New Client Order</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400" data-testid="new-order-close"><X size={18} /></button>
        </div>
        <div className="space-y-3">
          <Field label="Client Name" testId="ord-client"><input data-testid="ord-client-input" className="input-sm" value={form.client_name} onChange={e => set('client_name', e.target.value)} placeholder="Client name" /></Field>
          <Field label="SKU" testId="ord-sku">
            <select data-testid="ord-sku-select" className="input-sm" value={form.sku_id} onChange={e => set('sku_id', e.target.value)}>
              <option value="">Select SKU...</option>
              {skus.map(s => <option key={s.id} value={s.id}>{s.name} ({s.code})</option>)}
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Total Cartons" testId="ord-cartons"><input data-testid="ord-cartons-input" type="number" min="1" className="input-sm" value={form.total_cartons} onChange={e => set('total_cartons', e.target.value)} /></Field>
            <Field label="Pack Size (pcs/ctn)" testId="ord-pack"><input data-testid="ord-pack-input" type="number" min="1" className="input-sm" value={form.carton_pack_size} onChange={e => set('carton_pack_size', e.target.value)} /></Field>
          </div>
          <Field label="Delivery Due Date" testId="ord-due"><input data-testid="ord-due-input" type="date" className="input-sm" value={form.delivery_due_date} onChange={e => set('delivery_due_date', e.target.value)} /></Field>
          <Field label="Notes" testId="ord-notes"><textarea data-testid="ord-notes-input" className="input-sm h-16 resize-none" value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Optional" /></Field>
          {error && <p className="text-red-500 text-xs font-medium" data-testid="ord-error">{error}</p>}
        </div>
        <button data-testid="ord-submit" onClick={submit} disabled={saving || !valid} className="w-full mt-4 h-11 rounded-xl bg-indigo-500 text-white font-bold text-sm hover:bg-indigo-600 disabled:opacity-50 transition-colors">
          {saving ? <Loader2 size={16} className="animate-spin mx-auto" /> : 'Create Order'}
        </button>
      </div>
    </div>
  );
}

function OrderDetailDrawer({ order, mfg, onClose }) {
  const [prodEntries, setProdEntries] = useState([]);
  const [dispatches, setDispatches] = useState([]);
  useEffect(() => {
    mfg.get('/production-entries', { client_order_id: order.id }).then(r => setProdEntries(r.items || r || [])).catch(() => {});
    mfg.get('/dispatches', { client_order_id: order.id }).then(r => setDispatches(r.items || r || [])).catch(() => {});
  }, [order.id]);
  const p = order.progress || {};
  return (
    <Drawer open onClose={onClose} title={`Order ${order.order_no}`}>
      <div className="space-y-4 p-4">
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div><span className="text-[10px] uppercase text-gray-500 font-bold">Client</span><p className="font-semibold">{order.client_name}</p></div>
          <div><span className="text-[10px] uppercase text-gray-500 font-bold">SKU</span><p className="font-semibold">{order.sku_name || order.sku_code}</p></div>
          <div><span className="text-[10px] uppercase text-gray-500 font-bold">Due</span><p className="font-semibold">{order.delivery_due_date || '—'}</p></div>
          <div><span className="text-[10px] uppercase text-gray-500 font-bold">Pack Size</span><p className="font-semibold">{order.carton_pack_size} pcs/ctn</p></div>
        </div>
        <div className="bg-gray-50 rounded-xl p-3 text-sm space-y-1">
          <div className="flex justify-between"><span className="text-gray-500">Total</span><span className="font-bold">{order.total_cartons} ctns ({order.total_cartons * order.carton_pack_size} pcs)</span></div>
          <div className="flex justify-between"><span className="text-gray-500">Produced</span><span className="font-semibold text-emerald-600">{p.produced_cartons ?? 0} ctns ({p.produced_pieces ?? 0} pcs)</span></div>
          <div className="flex justify-between"><span className="text-gray-500">Dispatched</span><span className="font-semibold text-blue-600">{p.dispatched_cartons ?? 0} ctns</span></div>
          <div className="flex justify-between"><span className="text-gray-500">Balance</span><span className="font-bold text-amber-600">{p.balance_cartons ?? 0} ctns</span></div>
          <div className="flex justify-between"><span className="text-gray-500">Status</span><span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${STATUS_BADGE[p.status] || ''}`}>{(p.status || 'pending').replace(/_/g, ' ')}</span></div>
        </div>
        <div><h4 className="text-xs font-bold text-gray-500 uppercase mb-2">Production Entries ({prodEntries.length})</h4>
          {prodEntries.length === 0 ? <p className="text-gray-400 text-xs">None yet</p> : prodEntries.map(e => (
            <div key={e.id} className="text-xs flex justify-between py-1 border-b border-gray-50">
              <span>{e.production_date} · {e.unit_name || '—'}</span><span className="font-semibold">{e.quantity_pieces} pcs</span>
            </div>
          ))}
        </div>
        <div><h4 className="text-xs font-bold text-gray-500 uppercase mb-2">Dispatches ({dispatches.length})</h4>
          {dispatches.length === 0 ? <p className="text-gray-400 text-xs">None yet</p> : dispatches.map(e => (
            <div key={e.id} className="text-xs flex justify-between py-1 border-b border-gray-50">
              <span>{e.dispatch_date} · {e.destination_name || '—'}</span><span className="font-semibold">{e.quantity_cartons} ctns</span>
            </div>
          ))}
        </div>
      </div>
    </Drawer>
  );
}

function Field({ label, testId, children }) {
  return <label className="block"><span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">{label}</span><div className="mt-1">{children}</div></label>;
}
